import { Card, CardContent, CardHeader, CardTitle, cn } from '@data-delivery/d3s';
import type { UserEntity } from '@data-delivery/permission-ui-api';
import { useContentAccessRolesQuery } from '@data-delivery/permission-ui-data-access-state';
import React from 'react';

export interface UserDetailViewProps extends React.HTMLAttributes<HTMLDivElement> {
  user: UserEntity;
}

const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className='flex flex-col gap-0.5'>
    <span className='text-[11px] uppercase tracking-wide text-muted-foreground font-semibold'>
      {label}
    </span>
    <span className='text-sm font-medium break-words'>{value}</span>
  </div>
);

export function UserDetailView({ user, className, ...rest }: UserDetailViewProps) {
  const { data: contentAccessRoles } = useContentAccessRolesQuery();
  const contentAccessRoleDisplay = React.useMemo(() => {
    const roleId = user.contentAccessRole;
    if (!roleId) {
      return '—';
    }

    const match = contentAccessRoles?.find(r => r.id === roleId);
    return match?.name || roleId;
  }, [user.contentAccessRole, contentAccessRoles]);
  return (
    <Card className={cn('border-0', className)} {...rest}>
      <CardHeader>
        <CardTitle className='text-base font-semibold'>User Overview</CardTitle>
      </CardHeader>
      <CardContent className='space-y-6'>
        <div className='grid md:grid-cols-2 gap-4'>
          <DetailItem label='Login ID' value={user.loginId} />
          <DetailItem label='UMS ID' value={user.idmId} />
          <DetailItem label='Org Name' value={user.orgName || '—'} />
          <DetailItem label='Root Folder' value={user.rootFolder || '—'} />
          <DetailItem label='Landing Folder' value={user.landingFolder || '—'} />
          <DetailItem label='Service Account Owner' value={user.serviceAccountOwner || '—'} />
          <DetailItem label='SF Account ID' value={user.sfAccountId || '—'} />
          <DetailItem label='IP Whitelist' value={(user.ipWhitelist || []).join(', ') || '—'} />
          <DetailItem
            label='Distribution List'
            value={(user.distributionList || []).join(', ') || '—'}
          />
          <DetailItem
            label='Last Modified'
            value={user.lastModified ? new Date(user.lastModified).toLocaleString() : '—'}
          />
          <DetailItem label='Last Modified By' value={user.lastModifiedBy || '—'} />
          <DetailItem label='Content Access Role' value={contentAccessRoleDisplay} />
          <DetailItem label='Login As Admin' value={user.isLoginAdmin ? 'Yes' : 'No'} />
          <DetailItem label='Is Enable' value={user.isEnabled ? 'Yes' : 'No'} />
        </div>
      </CardContent>
    </Card>
  );
}
